import { TicketAssignKanban } from "../stores/TicketStore";
import SkTechApiClient from "./base/SkTechApiClient";
import dayjs from 'dayjs';

export type NewTicket = {
  companyName: string;
  companySystemName: string;

  companyId?: string;
  email: string;
  category: string;
  subject?: string;
  description?: string;
  userName?: string;
  userRole?: string;
  lang?: string;

}
export enum TicketStatus {
  New,
  Replied,
  NewMessage,
  ResolvedCustomer,
  ResolvedSupport
}

export enum TicketPriority {
  Low,
  Medium,
  High
}

export enum AuthorType {
  User=1,
  Support=2
}

export const getEnumName = <T extends Record<string, any>>(
  enumObj: T,
  value: T[keyof T]
): keyof T | any => {
  return Object.keys(enumObj).find(key => enumObj[key] === value) as keyof T | undefined;
}

export type Ticket = {
  id: string;
  publicId: string;
  subject: string;
  description: string;
  email: string;
  createdAt: Date;
  updatedAt: Date;
  closedAt: Date;
  category: string;
  source: number;
  status: TicketStatus;
  priority: TicketPriority;
  messages: TicketMessage[];
  userName?: string;
  lang?: string;
  userRole?: string;
  companySystemName: string;
  companyName: string;

  kanbanCompanyId: number;
  kanbanProjectId: number;
  kanbanResposibleUserId: number;
  kanbanTicketId: number;
  kanbanTicketPublicId: string;
}

export type TicketMessage = {
  id: number; 
  ticketId: number;
  email: string; 
  authorType: AuthorType;
  author: string; 
  role: string; 
  createdAt: Date; 
  isDeleted: boolean;
  isVisibleToUser: boolean;
  messageText: string;
  userName: string;
  attachments?: Attachment[];
};
export interface Attachment {
  id: number;
  ticketMessageId: number;
  fileName: string;
  fileUrl: string;
  secureUrl: string;
  publicId: string;
  fileType: string;
  fileSizeInBytes: number;
  uploadedAt: string; 
  uploadedByUserId: string;
  signature: string;
  resourceType: string; 
  etag: string;
  placeholder: boolean;
  assetId: string;
  assetFolder: string;
  displayName: string;
  version: string;
}
export default class TicketApi extends SkTechApiClient {
  private header = { 'X-Api-Key': 'e4021d40-74fc-41c5-9cd6-adb92ff98712' };

  public getAll = async (): Promise<Ticket[]> => {
    return await this.Get(`/api/Ticket`)
      .then(function (response) {
        if (response.status === 200 && response.data) {
          return response.data.map((ticket: Ticket) => {
            return {
              ...ticket,
              createdAt: dayjs.utc(ticket.createdAt).local().toDate(),
              updatedAt: dayjs.utc(ticket.updatedAt).local().toDate(),
              messages: ticket.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          });
        }
        return false;
      });
  }

  public getAllActive = async (): Promise<Ticket[]> => {
    return await this.Get(`/api/Ticket/active`)
      .then(function (response) {
        if (response.status === 200 && response.data) {
          return response.data.map((ticket: Ticket) => {
            return {
              ...ticket,
              createdAt: dayjs.utc(ticket.createdAt).local().toDate(),
              updatedAt: dayjs.utc(ticket.updatedAt).local().toDate(),
              messages: ticket.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          });
        }
        return false;
      });
  }

  public getAllClosed = async (): Promise<Ticket[]> => {
    return await this.Get(`/api/Ticket/closed`)
      .then(function (response) {
        if (response.status === 200 && response.data) {
          return response.data.map((ticket: Ticket) => {
            return {
              ...ticket,
              createdAt: dayjs.utc(ticket.createdAt).local().toDate(),
              updatedAt: dayjs.utc(ticket.updatedAt).local().toDate(),
              messages: ticket.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          });
        }
        return false;
      });
  }

  public getById = async (id: string): Promise<Ticket> => {
    return await this.Get(`/api/Ticket/${id}`)
      .then(function (response) {
        if (response.status === 200 && response.data) {
          return {...response.data,
            createdAt: dayjs.utc(response.data.createdAt).local().toDate(),
            updatedAt: dayjs.utc(response.data.updatedAt).local().toDate(),
            messages: response.data.messages.map((message: TicketMessage) => {
              return {
                ...message,
                createdAt: dayjs.utc(message.createdAt).local().toDate(),
              };
            }),
          };
        }
        return false;
      });
  }

  public getMyTickets = async (companyName: string, tenautOrEmail: string): Promise<Ticket[]> => { // Get tickets for current user or current company
    return await this.Get(`/api/Ticket/my?companyName=${companyName}&companySystemName=${tenautOrEmail}`)
      .then(function (response) {
        if (response.status === 200 && response.data) {
          return response.data.map((ticket: Ticket) => {
            return {
              ...ticket,
              createdAt: dayjs.utc(ticket.createdAt).local().toDate(),
              updatedAt: dayjs.utc(ticket.updatedAt).local().toDate(),
              messages: ticket.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          });;
        }
        return false;
      });
  } 
  public getMyTicketsActive = async (companyName: string, tenautOrEmail: string): Promise<Ticket[]> => { // Get tickets for current user or current company
    return await this.Get(`/api/Ticket/my/active?companyName=${companyName}&companySystemName=${tenautOrEmail}`)
      .then(function (response) {
        if (response.status === 200 && response.data ) {
          return response.data.map((ticket: Ticket) => {
            return {
              ...ticket,
              createdAt: dayjs.utc(ticket.createdAt).local().toDate(),
              updatedAt: dayjs.utc(ticket.updatedAt).local().toDate(),
              messages: ticket.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          });
        }
        return false;
      });
  }
  public getMyTicketsClosed = async (companyName: string, tenautOrEmail: string): Promise<Ticket[]> => { // Get tickets for current user or current company
    return await this.Get(`/api/Ticket/my/closed?companyName=${companyName}&companySystemName=${tenautOrEmail}`)
      .then(function (response) {
        if (response.status === 200 && response.data ) {
          return response.data.map((ticket: Ticket) => {
            return {
              ...ticket,
              createdAt: dayjs.utc(ticket.createdAt).local().toDate(),
              updatedAt: dayjs.utc(ticket.updatedAt).local().toDate(),
              messages: ticket.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          });;
        }
        return false;
      });
  }

  public add = async (item: NewTicket): Promise<any> => {
      return await this.Post(`/api/Ticket`, item, this.header)
        .then(function (response) {
          if (response.data) {
            return response.data;
          }
          return false;
        });
    }

    public assignKanbanTask = async (item: TicketAssignKanban): Promise<any> => {
      return await this.Post(`/api/Ticket/AssignTask`, item, this.header)
        .then(function (response) {
          if (response.data) {
            return response.data;
          }
          return false;
        });
    }
    public addMessage = async (ticketId: string, formData: FormData): Promise<any> => {
      return await this.Post(`/api/Ticket/${ticketId}/addMessage`, formData, this.header)
        .then(function (response) {
          if (response.data) {
            return response.data;
          }
          return false;
        });
    }
    
    public update = async (id: string, item: Ticket): Promise<any> => {
      return await this.Patch(`/api/Ticket/${id}`, item)
        .then(function (response) {
          if (response.status === 200) {
            return {...response.data,
              createdAt: dayjs.utc(response.data.createdAt).local().toDate(),
              updatedAt: dayjs.utc(response.data.updatedAt).local().toDate(),
              messages: response.data.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          }
          return false;
        });
    }

    public delete = async (id: number): Promise<any> => {
      return await this.Delete(`/api/Ticket/${id}`)
        .then(function (response) {
          if (response.status === 200) {
            return {...response.data,
              createdAt: dayjs.utc(response.data.createdAt).local().toDate(),
              updatedAt: dayjs.utc(response.data.updatedAt).local().toDate(),
              messages: response.data.messages.map((message: TicketMessage) => {
                return {
                  ...message,
                  createdAt: dayjs.utc(message.createdAt).local().toDate(),
                };
              }),
            };
          }
          return false;
        });
    }
  
}